// Story 7.12 Iteration 2 (ADO-911), DR-3 — the PDP's three accordion panels.
//
//   • Product Information — Code, EAN, Origin, Availability, VAT (REAL, Story 7.15
//                           + the review follow-up below)
//   • Stock Card          — the week Sales/Purchases table + the "in store"
//                           badge (REAL, Story 7.16 — see below)
//   • Nutritional Information — a "not available yet" note; the Typical Values
//                           table arrives with its producer (see below)
//
// Story 7.15 (ADO-1372) closed gap G5: Product Information is now REAL read-model
// data (Code · EAN · Origin · VAT — `cabinet_smanager.htm:2280-2412`, the panel this
// accordion ports). The fixture it replaced is gone, along with the claim that
// "Story 14.1 already emits them" — which was FALSE for EAN / Origin / VAT: none of
// the three had a producer at any layer, so 7.15 built the whole chain (feed →
// catalogue column → ProductChanged → projection → by-SKU read → here). The ordering
// WINDOW (`Available` + its countdown pill) went with it: the PO's 2026-07-30 answer
// makes the ordering window OFFER-scoped, which is Story 11.5's (`#1109`) — SL1.0's
// own Start/End rows are `promStart`/`promEnd`, a promotion window, not a
// product-master pair. That window stays gone.
//
// Review follow-up (ADO-1372, Peter Eastwood 2026-08-19): the approved Figma
// (`1169:7024`) shows an `Availability` row in this panel AND the field order
// Code · EAN · Origin · Availability · VAT. The row was re-added, and the fields
// reordered to the Figma grid. The `Availability` row an earlier pass retired was a
// FIXTURE that contradicted the real Story 7.3 count shown as the Stock Card's
// "N in store" badge; this one reads the SAME availability store the badge does, so
// the two can never diverge — and, per that 7.3 posture, an absent entry OMITS the
// row rather than fabricating a count.
//
// Story 7.21 (ADO-1673) completed the panel: Shelf Life, Supplier and Increase are now
// REAL read-model data, projected by Story 14.17 (ADO-1672, PR #475) and served on the
// same by-SKU read. Each obeys the same omit-when-absent posture (AC4) - a null value
// drops its whole row rather than rendering the "empty value" AC3 forbids. Supplier
// shows the feed-carried NAME where present, else the CODE (AC2); Shelf Life and
// Increase render as "N days" (AC3). The field order follows the approved Figma grid.
//
// Story 7.16 (ADO-1373) closed gap G2 the same way, for the Stock Card: the week
// table is REAL store EPOS movement now (Story 14.15's ingest + its
// `/api/v1/store-stock/stock-card` read), and its fixture is deleted from
// `productDetailSample.ts`. The panel body moved WHOLESALE into `StockCardContent`,
// because the category-listing card shows the same card in a popover and the two
// must not diverge — see that file for the read, the AC5
// absence posture, and why the DEPOT STOCK block is GONE rather than ported
// (Deviation D-2: no read model exposes a per-environment Ambient/Chill/WOW
// breakdown; Story 7.3 carries one `quantityOnHand` total per SKU per depot. Do
// not re-add fixture rows to satisfy AC4).
//
// The remaining FIXTURE on this file's panels:
//   Nutrition → Story 14.14 ingest (G1; Story 14.1 D10 disabled the importer)
//     and Story 7.17 display. Gated on the PO's Phase-1 decision.

import { useAvailabilityStore } from '../../availability/useAvailabilityStore';
import { PICKED_FRESH_MESSAGE, showsPickedFresh } from '../../availability/pickedFresh';
import {
  IN_STOCK_LABEL,
  isInStockForLabel,
  selectShowsStockQuantity,
} from '../../availability/showsStockQuantity';
import { useSessionStore } from '../../auth/useSessionStore';
import { useProductDetailStore } from '../../product-detail/useProductDetailStore';
import { Accordion } from './Accordion';
import { StockCardContent } from './StockCardContent';
import styles from './ProductPanels.module.css';

export interface ProductPanelsProps {
  readonly sku: string;
  readonly productName: string;
}

export function ProductPanels({ sku, productName }: ProductPanelsProps) {
  return (
    <div className={styles.panels}>
      <Accordion title="Product Information">
        <ProductInformation sku={sku} />
      </Accordion>
      <Accordion title="Stock Card">
        {/* Story 7.16 (ADO-1373) — REAL EPOS movement data now, shared verbatim
            with the category-listing popover so the two surfaces cannot diverge.
            The accordion unmounts its body when collapsed, which is what makes
            the read fire on OPEN rather than on page load. */}
        <StockCardContent sku={sku} productName={productName} />
      </Accordion>
      <Accordion title="Nutritional Information">
        <NutritionalInformation />
      </Accordion>
    </div>
  );
}

/**
 * Story 7.15 (ADO-1372) AC1/AC3 — Product Information: a label/value grid over
 * REAL read-model data, in the approved Figma grid order. Story 7.21 (ADO-1673) added
 * the last three (see the file header), so the full order is now:
 * Code · EAN · Origin · Availability · VAT · Shelf Life · Supplier · Increase
 * (approved Figma `4002-13427`, rev 6, authoritative over the older `1169:7024`).
 *
 * Reads the store's `detailBySku` slice directly (and the 7.3 availability store for
 * the Availability row), mirroring how `StockCard` below reads the availability
 * store — the detail fields are needed by exactly one of the three panels, so
 * threading them through `ProductPanels` would be a prop only one child uses. The
 * slice is loaded by the page for every arrival path, including a listing-card
 * handover (see `useProductDetailStore`).
 *
 * AC3: a field with no value is OMITTED entirely — no label, no empty `<dd>`, and
 * never an error. An empty grid (bar the always-present Code) is a legitimate render
 * for a product whose feed row carried none of the optional fields.
 */
function ProductInformation({ sku }: { readonly sku: string }) {
  const detail = useProductDetailStore((s) => s.detailBySku[sku]?.detail ?? null);
  // Story 7.3 — the SESSION depot's REAL stock on hand. This is the SAME store the
  // Stock Card's "N in store" badge reads (see `StockCard` below); wiring Availability
  // to that one source is what lets it live in BOTH panels — as the approved Figma
  // shows — without the two ever disagreeing, which is what retired the earlier
  // fixture version of this row.
  const availability = useAvailabilityStore((s) => s.entries[sku]);
  // Enabler E7-B (ADO-2273), AC15 restated by D-15: whether the row may print the count.
  const showsQuantity = useSessionStore(selectShowsStockQuantity);
  const fields: Array<{ readonly label: string; readonly value: string }> = [
    // `Code` is the SKU itself — SL1.0's `Code` IS the `CatItems` primary key, which
    // maps to `ProductProjection.sku` (D-2). The fixture this replaced invented a
    // hashed 6-digit code, which was WRONG DATA rather than placeholder data: it is
    // the value a customer quotes to order.
    { label: 'Code', value: sku },
  ];
  // AC3 is "blank/hidden, never an empty value", so the guard is PRESENCE, not
  // non-null: a whitespace-only value must omit the row rather than render a labelled
  // but visually empty `<dd>`. Blank-to-null is enforced by `nullIfEmpty` in the CSV
  // adapter and NOWHERE else on the chain — the projector is deliberately documented
  // and test-pinned as NOT canonicalising these three (so `'  IE  '` survives to the
  // wire verbatim), which puts the last guard here by design rather than by omission.
  //
  // Field ORDER follows the approved Figma grid (`4002-13427`):
  // Code · EAN · Origin · Availability · VAT · Shelf Life · Supplier · Increase
  // (the last three added by Story 7.21 / ADO-1673, appended after VAT).
  const ean = present(detail?.ean);
  if (ean !== null) fields.push({ label: 'EAN', value: ean });
  const origin = present(detail?.origin);
  if (origin !== null) fields.push({ label: 'Origin', value: formatOrigin(origin) });
  // 7.3 posture — an absent availability entry OMITS the row (AC3); a count is NEVER
  // fabricated for a SKU the store has not answered for ("unknown is never claimed as
  // a count"). `Math.max(0, …)` mirrors the badge's guard against a negative figure.
  //
  // Enabler E7-B (ADO-2273), AC15: the count prints only where the active entry shows a
  // quantity. Elsewhere the row reads SL1.0's "In stock" on a band with stock
  // (`stock_and_subs.php:87-88`), and is omitted on the out-of-stock band or a supplier block,
  // which `ProductHero`'s band pill and supplier-unavailable message already state.
  if (availability !== undefined) {
    if (showsQuantity) {
      fields.push({
        label: 'Availability',
        value: `${Math.max(0, availability.quantityOnHand)} currently in stock`,
      });
    } else if (isInStockForLabel(availability)) {
      fields.push({ label: 'Availability', value: IN_STOCK_LABEL });
    }
  } else if (detail !== null && showsPickedFresh(detail, availability)) {
    // Enabler E7-C (ADO-2274): a picked-fresh product has no entry (E7-B AVL-5.4); the row reads
    // SL1.0's detail message instead (`get_smanager.php:3906`, `cabinet_smanager.htm:2419`).
    fields.push({ label: 'Availability', value: PICKED_FRESH_MESSAGE });
  }
  const vat = formatVatRate(detail?.vatRate ?? null);
  if (vat !== null) fields.push({ label: 'VAT', value: vat });
  // Story 7.21 (ADO-1673) — Shelf Life · Supplier · Increase, projected by Story
  // 14.17 (ADO-1672) and appended after VAT in the approved Figma order (D-3). Each
  // obeys AC4: pushed only when its value is present.
  const shelfLife = formatDays(detail?.shelfLifeDays ?? null);
  if (shelfLife !== null) fields.push({ label: 'Shelf Life', value: shelfLife });
  // AC2 — the supplier NAME where the feed carried one (WOW extract), else the CODE.
  // `present()` also omits the row for a whitespace-only feed value: 14.17 leaves both
  // columns un-canonicalised, so this last guard is here, exactly as for EAN / Origin.
  const supplier = present(detail?.supplierName) ?? present(detail?.supplierCode);
  if (supplier !== null) fields.push({ label: 'Supplier', value: supplier });
  const increase = formatDays(detail?.orderIncreaseDays ?? null);
  if (increase !== null) fields.push({ label: 'Increase', value: increase });

  return (
    <dl className={styles.infoGrid}>
      {fields.map((field) => (
        <div key={field.label} className={styles.infoCell}>
          <dt>{field.label}</dt>
          <dd>{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Story 7.15 (ADO-1372), AC3 — a displayable value, or `null` if the field should be
 * omitted entirely. Whitespace-only counts as absent; the returned string is trimmed,
 * so no row ever renders leading/trailing padding from the feed.
 */
function present(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Story 7.15 (ADO-1372), D-8 — the stored value is a MULTIPLIER, not a percentage:
 * `'1.23'` means 23% VAT. Derived exactly as SL1.0 does
 * (`vatRateDisplay = (VatRate - 1) * 100`, `get_smanager.php:4017-4018`).
 *
 * ⚠️ The absence guard is `=== null`, NOT falsiness. A `1` multiplier is a genuine
 * ZERO-RATED product and must render `0%`; a falsy check would make it vanish and
 * silently claim "no VAT information" about a product that has some.
 *
 * A multiplier BELOW 1 is not zero-rated, it is nonsense — `(0 - 1) * 100` is `-100`,
 * and a PDP must not print a negative tax rate. The primary defence is catalogue's
 * activation merge, which publishes a rate outside [1, 2] as null and reports it
 * (Enabler E7-A, ADO-2272, D-13), so such a value never reaches the product page from
 * the feed. This second guard covers any other producer: out-of-domain rates are
 * OMITTED (AC3) rather than displayed. An earlier
 * version of this file — and of D-8 — claimed `0` was a legitimate zero-rated
 * multiplier; it is not, and the claim is deleted rather than softened.
 *
 * The rounding is not cosmetic: `(1.23 - 1) * 100` is `22.999999999999996` in IEEE
 * doubles, so an unrounded render would read "22.999999999999996%".
 */
function formatVatRate(rate: string | null): string | null {
  if (rate === null) return null;
  const multiplier = Number(rate);
  if (!Number.isFinite(multiplier)) return null;
  if (multiplier < 1 || multiplier > 2) return null;
  const percent = Math.round((multiplier - 1) * 100 * 100) / 100;
  return `${percent}%`;
}

/**
 * Story 7.21 (ADO-1673), AC3/AC4 — a whole-day count rendered as SL1.0's "N days"
 * (`get_smanager.php:2387`), or `null` when the row should be OMITTED. Mirrors
 * `formatVatRate`'s absence posture: `null`, a non-finite value, and a NEGATIVE value
 * all omit the row (AC4 — an out-of-domain legacy row is hidden, never shown as
 * "-3 days"). `0` is a legitimate value and renders "0 days". "1 day" is singularised
 * as polish; AC3's literal format is "N days".
 *
 * The guard is `=== null`, NOT falsiness: `0` must survive, exactly as `formatVatRate`
 * guards the zero-rated `1` multiplier from a falsy check.
 */
function formatDays(days: number | null): string | null {
  if (days === null) return null;
  if (!Number.isFinite(days) || days < 0) return null;
  return `${days} ${days === 1 ? 'day' : 'days'}`;
}

// Hoisted, not built per render: `Intl.DisplayNames` is expensive to construct and
// this panel re-renders with the accordion.
const REGION_NAMES = new Intl.DisplayNames(['en'], { type: 'region' });

/**
 * Story 7.15 (ADO-1372), D-7 — the ISO-3166-1 alpha-2 codes that are CURRENTLY
 * OFFICIALLY ASSIGNED to a country or territory (249 of them).
 *
 * This list, not `Intl.DisplayNames`, is what decides whether a stored value is a
 * country at all. `Intl` is then asked only for its NAME. The split matters because
 * CLDR resolves far more than countries, and a code-review pass (2026-08-03) measured
 * what it does with the rest:
 *
 *   - deprecated codes silently become a DIFFERENT, successor country —
 *     `AN` → "Curaçao", `SU` → "Russia", `YU`/`CS` → "Serbia", `ZR` → "Congo -
 *     Kinshasa", `TP` → "Timor-Leste"
 *   - placeholder and grouping codes resolve to labels that read like origins —
 *     `ZZ` → "Unknown Region", `EU` → "European Union", `EZ` → "Eurozone",
 *     `QO` → "Outlying Oceania", `XA` → "Pseudo-Accents"
 *   - numeric region codes resolve to continents — `001` → "world", `150` → "Europe"
 *
 * `fallback: 'code'` does NOT protect against any of these: it only covers codes that
 * are structurally well-formed but unassigned (`QX` → `QX`). Every case above resolves,
 * so the verbatim fallback was bypassed — which is why the earlier `.of('ZZ')` returns
 * `'ZZ'` comment was wrong, and why the demo check of `QX` passed while the class of
 * bug went unseen. Country of origin is a labelling claim on a wholesaler's PDP, so
 * printing "Curaçao" for a Netherlands-Antilles code is worse than printing the code.
 *
 * BWG's `p_country` alphabet is still UNPROVEN (D-7) — the point of the allowlist is
 * that it does not need to be known. Anything outside the list renders verbatim, so
 * the display is correct under any alphabet and needs no rework if BWG's
 * `CountryOfOrigin` table later arrives.
 */
const ASSIGNED_ALPHA2 = new Set(
  (
    'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN ' +
    'BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ ' +
    'DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL ' +
    'GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM ' +
    'JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME ' +
    'MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP ' +
    'NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD ' +
    'SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO ' +
    'TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
  ).split(' '),
);

/**
 * Story 7.15 (ADO-1372), D-7 — resolve the stored origin CODE to a country name,
 * falling back to the raw stored value VERBATIM.
 *
 * SL1.0 displays a resolved name (`LEFT JOIN CountryOfOrigin … CountryName`), so
 * "show a country name" is the settled requirement — but that lookup table is BWG
 * reference data we do not have. So: resolve ONLY codes on {@link ASSIGNED_ALPHA2},
 * and print anything else exactly as stored. Never fabricates a country, for real
 * this time — see the allowlist's own comment for what the previous
 * `Intl`-decides-everything version did instead.
 *
 * A value that is blank once trimmed is treated as ABSENT by the caller, so it never
 * reaches here.
 */
function formatOrigin(code: string): string {
  const normalised = code.trim().toUpperCase();
  if (!ASSIGNED_ALPHA2.has(normalised)) return code.trim();
  // Guarded by the allowlist, so `.of` neither throws (well-formed) nor returns
  // `undefined` (assigned) — the `??` is belt-and-braces against a runtime whose CLDR
  // data is trimmed down.
  return REGION_NAMES.of(normalised) ?? normalised;
}

/** Nutritional Information (gap G1 → Story 14.14 ingest + 7.17 display).
 *  DR-3 requires the panel; there is no producer for the values, so it carries
 *  an explicit "not available" note rather than SKU-hashed energy/salt figures,
 *  which are allergen-adjacent and a user could act on and be wrong.
 *
 *  There is deliberately NO table markup waiting behind an `if`: with no
 *  producer it would be unreachable, untestable code shaped to a guess. The
 *  Typical Values / Per 100g / Per serving / RI* / %RI* grid gets built against
 *  what the 14.14 ingest actually emits. */
function NutritionalInformation() {
  return (
    <p className={styles.emptyNote}>
      Nutritional information isn’t available for this product yet.
    </p>
  );
}
